/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex, jsx-a11y/click-events-have-key-events */
import classnames from 'classnames';
import { useMemo } from 'react';

import { useToggle } from '../../../../hooks';
import { Icon, icons } from '../../../generic';

import { AuthenticationMode } from './types';

type AuthenticationModeDescription = {
  mode: AuthenticationMode;
  description: string;
};

const authenticationModes: AuthenticationModeDescription[] = [
  {
    mode: AuthenticationMode.Email,
    description:
      'Users may sign into your FOSSA organization with a password, Google, GitHub, GitLab, or Bitbucket. Users must be invited to join your organization.',
  },
  {
    mode: AuthenticationMode.SsoEmail,
    description:
      'Users may automatically join your FOSSA organization by authenticating through your organization\'s SSO. Users may also be invited and sign in using a password, Google, GitHub, GitLab, or Bitbucket.',
  },
  {
    mode: AuthenticationMode.Sso,
    description:
      'All users must authenticate through your organization\'s SSO to join or sign into your FOSSA organization. No invites are required. Password authentication is disabled.',
  },
  {
    mode: AuthenticationMode.OAuth,
    description:
      'All users must authenticate using either Google, GitHub, GitLab, or Bitbucket to sign into your FOSSA organization. Password authentication is disabled. Users must be invited to join.',
  },
];

type AuthenticationModePickerProps = {
  mode: AuthenticationMode;
  onSelectMode: (mode: AuthenticationMode) => void;
};

const AuthenticationModePicker = (props: AuthenticationModePickerProps) => {
  const [isToggled, onToggle] = useToggle();
  const activeAuthMode = useMemo(() => authenticationModes.find(
    ({ mode }: AuthenticationModeDescription) => mode === props.mode,
  ) as AuthenticationModeDescription, [props.mode]);

  const onSetMode = (mode: AuthenticationMode) => () => {
    onToggle();
    props.onSelectMode(mode);
  };

  const unselectedItems = authenticationModes.map(
    ({ mode, description }) =>
      activeAuthMode.mode !== mode && (
        <div
          key={mode}
          className={classnames('auth-settings__mode-picker__item')}
          onClick={onSetMode(mode)}
          tabIndex={0}
        >
          <div>
            <b>{mode}</b>
            <p>{description}</p>
          </div>
          <div className="auth-settings__mode-picker__spacer" />
        </div>
      ),
  );

  return (
    <div className="auth-settings__mode-picker">
      <div className="auth-settings__mode-picker__all">
        <div
          className={classnames('auth-settings__mode-picker__item', 'auth-settings__mode-picker__item--active', {
            'auth-settings__mode-picker__item--open': isToggled,
          })}
          onClick={onToggle}
          tabIndex={0}
        >
          <div>
            <b>{activeAuthMode.mode}</b>
            <p>{activeAuthMode.description}</p>
          </div>
          <div
            className={classnames('auth-settings__mode-picker__toggler', {
              'auth-settings__mode-picker__toggler--active': isToggled,
            })}
          >
            <Icon icon={icons.angleDown} />
          </div>
        </div>
        {isToggled && unselectedItems}
      </div>
    </div>
  );
};

export default AuthenticationModePicker;
